import React from 'react';
import { staticImage } from '../constants';
import { useTranslation } from '../hooks/useTranslation';

interface WelcomeScreenProps {
  language: string;
  user?: string;
  onSignupClick: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  language,
  user,
  onSignupClick
}) => {
  const { t } = useTranslation(language);
  const features = [
    {
      icon: '🌍',
      text: t('welcome.features.globalMarket')
    },
    {
      icon: '🎯',
      text: t('welcome.features.personalized')
    },
    {
      icon: '⚡',
      text: t('welcome.features.instantCheckout')
    }
  ];

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f8f8f8',
      borderRadius: '15px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header Section */}
      <div style={{
        marginBottom: '4px'
      }}>
        <p style={{
          margin: 0,
          color: '#333',
          fontSize: '15px',
          lineHeight: '1.6'
        }}>
          {t('welcome.intro')}
        </p>
      </div>

      {/* Features List */}
      <div style={{ marginBottom: '5px' }}>
        {features.map((feature, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '0',
            color: '#666'
          }}>
            <span style={{ fontSize: '16px' }}>{feature.icon}</span>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {feature.text}
            </span>
          </div>
        ))}
      </div>

      {/* Example Queries */}
      <div style={{ marginBottom: '5px' }}>
        <h4 style={{
          color: '#333',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {t('welcome.trendingPrompts')}
        </h4>
        
        {/* Good Examples */}
        <div style={{ marginBottom: '5px' }}>
          <div style={{
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            fontSize: '12px',
            fontStyle: 'italic',
            lineHeight: '1.6',
            color: '#000',
          }}>
            { language === 'zh-TW' && <>
              <div>1. 請推薦最優惠報價（註明酒類品牌 + 指定特定年份或範圍皆可，例如：<b>紅顏容酒莊 2010-2019</b>）</div>
              <div>2. 我想購買<b>1990年份的波爾多紅酒</b>，預算約$1500。</div>
              <div>3. 請給我推薦勃艮第村田白酒，<b>價格$600以下</b>。</div>
              <div>4. 請建議<b>現在適宜飲用</b>的超級托斯卡納紅酒最優惠報價。</div>
            </> }
            { language === 'zh-CN' && <>
              <div>1. 推荐最优惠报价（注明酒类品牌 + 指定特定年份或范围皆可，例如：<b>红颜容酒庄 2010-2019</b>）</div>
              <div>2. 我想购买<b>1990年份的波尔多红酒</b>，预算约$1500。 </div>
              <div>3. 请给我推荐勃艮第村田白酒，<b>价格$600以下</b>。</div>
              <div>4. 请建议<b>现在适宜饮用</b>的超级托斯卡纳红酒最优惠报价。</div>
            </> }
            { language === 'ja' && <>
              <div>1. ベストオファーをおすすめください（ワインブランド + ヴィンテージ 例：<b>オー・ブリオン 2010-2019</b>）</div>
              <div>2. <b>1990年代のボルドー赤</b>を約$1500で購入したい</div>
              <div>3. ブルゴーニュ村名白 <b>$600以下</b></div>
              <div>4. スーパートスカーナ赤 <b>今飲み頃</b> 約$1000のベストオファー</div>
            </> }
            { language === 'ko' && <>
              <div>1. 베스트 오퍼를 추천해 주세요 (와인 브랜드 + 빈티지 예: <b>오 브리옹 2010-2019</b>)</div>
              <div>2. <b>1990년대 보르도 레드</b> 약 $1500에 구매하고 싶습니다</div>
              <div>3. 부르고뉴 빌라주 화이트 <b>$600 이하</b></div>
              <div>4. 슈퍼 투스칸 레드 <b>지금 마시기 좋은</b> 약 $1000 베스트 오퍼</div>
            </> }
            { language === 'da' && <>
              <div>1. Anbefal venligst bedste tilbud (specifikt vinmærke + årgang f.eks. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Jeg vil gerne købe <b>1990'erne Bordeaux rød</b> ca. $1500</div>
              <div>3. Bourgogne landsby hvid <b>under $600</b></div>
              <div>4. Bedste tilbud Super Tuscan rød <b>klar til at drikke nu</b> ca. $1000</div>
            </> }
            { language === 'de' && <>
              <div>1. Bitte empfehlen Sie das beste Angebot (Weinmarke + Jahrgang z.B. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Ich möchte <b>1990er Bordeaux Rot</b> ca. $1500 kaufen</div>
              <div>3. Burgunder Dorfwein weiß <b>unter $600</b></div>
              <div>4. Bestes Angebot Super Tuscan rot <b>jetzt trinkfertig</b> ca. $1000</div>
            </> }
            { language === 'es' && <>
              <div>1. Recomiende la mejor oferta (marca de vino + añada ej. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Quiero comprar <b>Burdeos tinto de los 90</b> alrededor de $1500</div>
              <div>3. Borgoña blanco de pueblo <b>menos de $600</b></div>
              <div>4. Mejor oferta Super Toscano tinto <b>listo para beber</b> alrededor de $1000</div>
            </> }
            { language === 'fr' && <>
              <div>1. Recommandez la meilleure offre (marque de vin + millésime ex. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Je veux acheter un <b>Bordeaux rouge des années 90</b> environ $1500</div>
              <div>3. Bourgogne blanc village <b>moins de $600</b></div>
              <div>4. Meilleure offre Super Toscan rouge <b>prêt à boire</b> environ $1000</div>
            </> }
            { language === 'it' && <>
              <div>1. Consiglia la migliore offerta (marca di vino + annata es. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Voglio comprare <b>Bordeaux rosso anni '90</b> circa $1500</div>
              <div>3. Borgogna bianco village <b>sotto $600</b></div>
              <div>4. Migliore offerta Super Tuscan rosso <b>pronto da bere</b> circa $1000</div>
            </> }
            { language === 'nl' && <>
              <div>1. Beveel de beste aanbieding aan (specifiek wijnmerk + jaargang bijv. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Ik wil <b>1990's Bordeaux rood</b> rond $1500 kopen</div>
              <div>3. Bourgogne dorps wit <b>onder $600</b></div>
              <div>4. Beste aanbieding Super Tuscan rood <b>klaar om te drinken</b> rond $1000</div>
            </> }
            { language === 'pt' && <>
              <div>1. Recomende a melhor oferta (marca de vinho + colheita ex. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. Quero comprar <b>Bordeaux tinto dos anos 90</b> cerca de $1500</div>
              <div>3. Borgonha branco village <b>abaixo de $600</b></div>
              <div>4. Melhor oferta Super Toscano tinto <b>pronto a beber</b> cerca de $1000</div>
            </> }
            { language === 'th' && <>
              <div>1. แนะนำข้อเสนอที่ดีที่สุด (แบรนด์ไวน์ + วินเทจ เช่น <b>Haut Brion 2010-2019</b>)</div>
              <div>2. ต้องการซื้อ<b>บอร์โดซ์แดงยุค 1990</b> ประมาณ $1500</div>
              <div>3. เบอร์กันดีขาวระดับหมู่บ้าน <b>ต่ำกว่า $600</b></div>
              <div>4. ข้อเสนอที่ดีที่สุด Super Tuscan แดง <b>พร้อมดื่มตอนนี้</b> ประมาณ $1000</div>
            </> }
            { !['zh-TW', 'zh-CN', 'ja', 'ko', 'da', 'de', 'es', 'fr', 'it', 'nl', 'pt', 'th'].includes(language) && <>
              <div>1. Please recommend best offer (specific wine brand + vintage eg. <b>Haut Brion 2010-2019</b>)</div>
              <div>2. I want to buy <b>1990s Bordeaux Red</b> around $1500</div>
              <div>3. Burgundy village white <b>below $600</b></div>
              <div>4. Best offer Super Tuscan red <b>ready to drink now</b> around $1000</div>
            </> }
          </div>
        </div>

        {/* Question */}
        <div style={{ marginBottom: '5px' }}>
          <p style={{
            margin: 0,
            color: '#333',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            {t('welcome.question')}
          </p>
        </div>
      </div>

    </div>
  );
};

export default WelcomeScreen;